import { GAME_FILTER_SUPPORT_TYPE } from 'constants/uiConfig'
import { sortStudiosByName } from 'utils/studio'

/**
 * Provider utilities
 *
 * Contains helpers related to Provider entities.
 */

/**
 * Returns a new array of providers sorted by name A → Z (case-insensitive).
 *
 * - Safely handles null/undefined inputs by returning an empty array.
 * - Does not mutate the original array.
 *
 * @param {Array<{name?: string}>} providers
 * @returns {Array}
 */
export const sortProvidersByName = (providers) => {
  const list = Array.isArray(providers) ? [...providers] : []
  return list.sort((a, b) => {
    const an = (a?.providerDisplayName || a?.name || '').toString().trim()
    const bn = (b?.providerDisplayName || b?.name || '').toString().trim()
    return an.localeCompare(bn, undefined, { sensitivity: 'base' })
  })
}

/**
 * Expands a list of providers into a flat list that also includes their
 * studios, for studios where `showAsProvider` is true. Used to build the
 * dataset shown in the provider filter checkbox list.
 *
 * - The provider itself is omitted from the result when it has `hideProvider: true`,
 *   though its eligible studios are still included.
 * - The provider is also omitted when one of its own studios shares its name
 *   and is shown as a provider (`showAsProvider: true`), to avoid listing the
 *   same provider twice under two different entries.
 * - Safely handles null/undefined inputs by returning an empty array.
 * - Does not mutate the original array.
 *
 * @param {Array<Object>} providers - Providers, each optionally carrying a `TenantGameStudios` array and a `hideProvider` flag.
 * @returns {Array<Object>} Flat list of providers followed by their eligible studios.
 */
export const expandProvidersWithStudios = (providers) => {
  const providerStudiosMap = []
  if (providers?.length > 0) {
    providers.forEach(provider => {
      const { TenantGameStudios = [], hideProvider = false, name } = provider
      const providerName = (name || '').toString().toLowerCase()
      const hasSameNameStudioAsProvider = TenantGameStudios?.some(studio => {
        const studioName = (studio?.GameStudio?.name || '').toString().toLowerCase()
        return studio?.showAsProvider && !!studioName && studioName === providerName
      })
      if (!hideProvider && !hasSameNameStudioAsProvider) {
        providerStudiosMap.push(provider)
      }
      if (TenantGameStudios?.length > 0) {
        TenantGameStudios.forEach(studio => {
          if (studio?.showAsProvider) {
            providerStudiosMap.push({
              thumbnailUrl: provider?.thumbnailUrl,
              id: studio?.id,
              providerId: studio?.id,
              name: studio?.GameStudio?.name,
              providerDisplayName: studio?.name,
              showAsProvider: studio?.showAsProvider
            })
          }
        })
      }
    })
  }
  return providerStudiosMap
}

/**
 * Resolves the display name to show for a game's provider/studio, based on
 * the tenant's game filter support type.
 *
 * - When filtering by PROVIDER: prefers the studio name if the game should
 *   be shown as a provider (`showAsProvider`), otherwise the provider name.
 * - Otherwise: prefers the studio name, falling back to the provider name.
 * - Each preference falls back from display name → name.
 *
 * @param {Object} game - Game entity, optionally carrying studio/provider name fields.
 * @param {string} gameFilterSupportType - One of GAME_FILTER_SUPPORT_TYPE.
 * @returns {string|undefined}
 */
export const getProcessedProviderName = (game, gameFilterSupportType) => {
  const studioName = game?.studioDisplayName || game?.studioName
  const providerName = game?.providerDisplayName || game?.providerName

  if (gameFilterSupportType === GAME_FILTER_SUPPORT_TYPE.PROVIDER) {
    return game?.showAsProvider ? studioName : providerName
  }

  return studioName || providerName
}

/**
 * Returns a provider's studios limited to the allowed studio IDs, sorted by name.
 *
 * - When `allowedStudioIds` is empty, every studio of the provider is allowed.
 * - Safely handles a missing provider or `TenantGameStudios` by returning an empty array.
 *
 * @param {Object} provider - Provider carrying an optional `TenantGameStudios` array.
 * @param {Set<number>} [allowedStudioIds] - Studio IDs allowed for the current country/category.
 * @returns {Array<Object>}
 */
export const getAllowedProviderStudios = (provider, allowedStudioIds = new Set()) => {
  const studios = Array.isArray(provider?.TenantGameStudios) ? provider.TenantGameStudios : []
  const allowedStudios = allowedStudioIds.size ? studios.filter(studio => allowedStudioIds.has(studio?.id)) : studios
  return sortStudiosByName(allowedStudios)
}

/**
 * Checks whether a provider matches a search query.
 *
 * - Matches on the tenant display name (`providerDisplayName`) or the original `name`,
 *   so renamed providers are found by both.
 * - Matching is case-insensitive and ignores surrounding whitespace.
 * - An empty query matches every provider.
 *
 * @param {Object} provider
 * @param {string} searchQuery
 * @returns {boolean}
 */
export const matchesProviderSearch = (provider, searchQuery) => {
  const query = (searchQuery || '').trim().toLowerCase()
  if (!query) return true
  return [provider?.providerDisplayName, provider?.name]
    .some(providerName => (providerName || '').toString().toLowerCase().includes(query))
}

/**
 * Filters providers by a search query, matching either the provider or one of its allowed studios.
 *
 * - A provider matches on its tenant display name (`providerDisplayName`) or its original `name`,
 *   so renamed providers are found by both.
 * - A studio matches on its tenant display name (`name`).
 * - Matching is case-insensitive and ignores surrounding whitespace.
 * - Each returned provider carries `__matchedStudios` with the studios that matched the query.
 * - An empty query returns the providers unchanged.
 *
 * @param {Array<Object>} providers
 * @param {string} searchQuery
 * @param {Set<number>} [allowedStudioIds] - Studio IDs allowed for the current country/category.
 * @returns {Array<Object>}
 */
export const filterProvidersBySearch = (providers, searchQuery, allowedStudioIds) => {
  const query = (searchQuery || '').trim().toLowerCase()
  if (!query) return providers
  const matchesQuery = (value) => (value || '').toString().toLowerCase().includes(query)

  return (providers || []).map(provider => {
    const providerMatch = matchesProviderSearch(provider, query)
    const matchedStudios = getAllowedProviderStudios(provider, allowedStudioIds).filter(studio => matchesQuery(studio?.name))
    return providerMatch || matchedStudios.length > 0 ? { ...provider, __matchedStudios: matchedStudios } : null
  }).filter(Boolean)
}
